import { useCallback, useEffect, useRef, useState } from 'react';
import {
  streamHostAssistant,
  type HostAssistantFact,
  type HostAssistantTurn,
} from '../../../services/host-assistant-client';
import { describeError, type AssistantError } from './assistantErrors';

export const MAX_HISTORY_TURNS = 20;
export const MAX_TURN_CHARS = 4000;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  facts?: HostAssistantFact[];
}

/** The backend accepts at most 20 turns of ≤ 4000 chars; send the most recent ones. */
export function buildHistory(messages: ChatMessage[]): HostAssistantTurn[] {
  return messages.slice(-MAX_HISTORY_TURNS).map((m) => ({
    role: m.role,
    content: m.content.slice(0, MAX_TURN_CHARS),
  }));
}

const storageKey = (groupId: string) => `tripsby_ask_history:${groupId}`;

function loadMessages(groupId: string): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(storageKey(groupId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        !!m && typeof m.id === 'string' && typeof m.content === 'string' && (m.role === 'user' || m.role === 'assistant'),
    );
  } catch {
    return [];
  }
}

function saveMessages(groupId: string, messages: ChatMessage[]) {
  try {
    if (messages.length) sessionStorage.setItem(storageKey(groupId), JSON.stringify(messages));
    else sessionStorage.removeItem(storageKey(groupId));
  } catch {
    /* storage blocked or full — the conversation just won't survive a reload */
  }
}

let counter = 0;
const nextId = () => `m${Date.now().toString(36)}${(counter++).toString(36)}`;

/**
 * Conversation state for one group. Mount it per group (the panel is keyed by
 * group id) so history never leaks between groups.
 */
export function useAskTripsby(groupId: string, tab: string | undefined) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessages(groupId));
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<AssistantError | null>(null);

  const messagesRef = useRef(messages);
  const abortRef = useRef<AbortController | null>(null);
  const runRef = useRef(0);
  const pendingRef = useRef(false);
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const commit = useCallback(
    (next: ChatMessage[]) => {
      messagesRef.current = next;
      setMessages(next);
      saveMessages(groupId, next);
    },
    [groupId],
  );

  const run = useCallback(
    (question: string, base: ChatMessage[], appendUser: boolean) => {
      const history = buildHistory(base);
      if (appendUser) commit([...base, { id: nextId(), role: 'user', content: question }]);

      const controller = new AbortController();
      abortRef.current = controller;
      const runId = ++runRef.current;
      const current = () => runRef.current === runId;

      pendingRef.current = true;
      setPending(true);
      setStatus(null);
      setError(null);

      streamHostAssistant(
        {
          group_id: groupId,
          message: question,
          ...(history.length ? { history } : {}),
          ...(tabRef.current ? { tab: tabRef.current } : {}),
        },
        { signal: controller.signal, onStatus: (m) => current() && setStatus(m) },
      )
        .then((result) => {
          if (!current()) return;
          commit([
            ...messagesRef.current,
            { id: nextId(), role: 'assistant', content: result.response, ...(result.facts ? { facts: result.facts } : {}) },
          ]);
        })
        .catch((err) => {
          if (current()) setError(describeError(err));
        })
        .finally(() => {
          if (!current()) return;
          pendingRef.current = false;
          setPending(false);
          setStatus(null);
        });
    },
    [commit, groupId],
  );

  const send = useCallback(
    (raw: string) => {
      const question = raw.trim();
      if (!question || pendingRef.current) return;
      run(question, messagesRef.current, true);
    },
    [run],
  );

  /** Re-ask the last question without duplicating it in the transcript. */
  const retry = useCallback(() => {
    const all = messagesRef.current;
    const last = all[all.length - 1];
    if (!last || last.role !== 'user') return;
    run(last.content, all.slice(0, -1), false);
  }, [run]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    runRef.current++; // orphan any in-flight answer
    commit([]);
    setError(null);
    pendingRef.current = false;
    setPending(false);
    setStatus(null);
  }, [commit]);

  useEffect(
    () => () => {
      runRef.current++;
      abortRef.current?.abort();
    },
    [],
  );

  return { messages, pending, status, error, send, retry, stop, reset };
}
